import { useEffect } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import { queryClient } from '@/providers/QueryProvider';

export interface WsEvent {
  type: string;
  data?: Record<string, unknown>;
  timestamp?: string;
}

// Shared state via Zustand — ensures singleton behavior across components
interface WsState {
  isConnected: boolean;
  onlineAgents: string[];
  lastEvent: WsEvent | null;
  setConnected: (v: boolean) => void;
  setOnlineAgents: (agents: string[]) => void;
  addOnlineAgent: (id: string) => void;
  removeOnlineAgent: (id: string) => void;
  setLastEvent: (e: WsEvent) => void;
}

const useWsStore = create<WsState>((set) => ({
  isConnected: false,
  onlineAgents: [],
  lastEvent: null,
  setConnected: (v) => set({ isConnected: v }),
  setOnlineAgents: (agents) => set({ onlineAgents: agents }),
  addOnlineAgent: (id) =>
    set((s) => ({
      onlineAgents: s.onlineAgents.includes(id) ? s.onlineAgents : [...s.onlineAgents, id],
    })),
  removeOnlineAgent: (id) =>
    set((s) => ({ onlineAgents: s.onlineAgents.filter((a) => a !== id) })),
  setLastEvent: (e) => set({ lastEvent: e }),
}));

const WS_BASE = import.meta.env.VITE_WS_URL
  || (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;

// Module-level singleton tracking — only one connection regardless of how many
// components call useWebSocket().
let wsInstance: WebSocket | null = null;
let ownerCount = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let pingInterval: ReturnType<typeof setInterval> | undefined;
let reconnectAttempt = 0;

function handleEvent(event: WsEvent) {
  const store = useWsStore.getState();
  store.setLastEvent(event);

  switch (event.type) {
    case 'ticket:created':
    case 'ticket:updated':
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'actividad-reciente'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      break;

    case 'conversacion:nueva':
    case 'conversacion:mensaje':
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'metrics'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'actividad-reciente'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      break;

    case 'notificacion:nueva':
      queryClient.invalidateQueries({ queryKey: ['notificaciones', 'count'] });
      break;

    case 'agents:list':
      if (Array.isArray(event.data?.user_ids)) {
        store.setOnlineAgents(event.data!.user_ids as string[]);
      }
      break;

    case 'agent:online': {
      const userId = event.data?.user_id as string | undefined;
      if (userId) store.addOnlineAgent(userId);
      break;
    }

    case 'agent:offline': {
      const userId = event.data?.user_id as string | undefined;
      if (userId) store.removeOnlineAgent(userId);
      break;
    }

    case 'pong':
      break;
  }
}

function connectWs(accessToken: string) {
  if (wsInstance?.readyState === WebSocket.OPEN) return;

  // Close stale connection if any
  if (wsInstance) {
    try { wsInstance.close(); } catch { /* noop */ }
  }

  const url = `${WS_BASE}/ws?token=${encodeURIComponent(accessToken)}`;
  const ws = new WebSocket(url);
  wsInstance = ws;

  ws.onopen = () => {
    useWsStore.getState().setConnected(true);
    reconnectAttempt = 0;

    // Keepalive ping every 30s
    pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30_000);
  };

  ws.onmessage = (e) => {
    let event: WsEvent;
    try {
      event = JSON.parse(e.data) as WsEvent;
    } catch {
      console.warn('[useWebSocket] Received non-JSON message, ignoring');
      return;
    }
    // Handler errors must surface, not be silently caught
    handleEvent(event);
  };

  ws.onclose = (closeEvent) => {
    useWsStore.getState().setConnected(false);
    if (pingInterval) clearInterval(pingInterval);

    const delay = Math.min(1000 * 2 ** reconnectAttempt, 30_000);
    // Cap counter so delay stabilizes at 30s
    reconnectAttempt = Math.min(reconnectAttempt + 1, 10);

    console.warn(
      `[useWebSocket] Closed (code=${closeEvent.code}). Reconnecting in ${delay}ms (attempt ${reconnectAttempt})`
    );

    if (ownerCount <= 0) return;

    reconnectTimer = setTimeout(() => {
      // Read fresh token at reconnect time (may have been refreshed since close)
      const token = useAuthStore.getState().accessToken;
      if (token) {
        connectWs(token);
      }
      // If no token, useWebSocket effect will reconnect once token arrives
    }, delay);
  };

  ws.onerror = (errorEvent) => {
    console.error('[useWebSocket] WebSocket error:', errorEvent);
    // onclose fires after onerror in most environments — reconnect handled there
  };
}

function disconnectWs() {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  if (pingInterval) clearInterval(pingInterval);
  if (wsInstance) {
    try { wsInstance.close(); } catch { /* noop */ }
    wsInstance = null;
  }
  reconnectAttempt = 0;
  useWsStore.getState().setConnected(false);
}

/**
 * Singleton WebSocket hook. Multiple components can call this —
 * only one connection is maintained via module-level state.
 */
export function useWebSocket() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isConnected = useWsStore((s) => s.isConnected);
  const onlineAgents = useWsStore((s) => s.onlineAgents);
  const lastEvent = useWsStore((s) => s.lastEvent);

  // Track how many mounted components use this hook
  useEffect(() => {
    ownerCount += 1;
    return () => {
      ownerCount -= 1;
      if (ownerCount <= 0) {
        disconnectWs();
      }
    };
  }, []);

  // Connect/reconnect when token changes
  useEffect(() => {
    if (!accessToken || !isAuthenticated) {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      return;
    }
    // Reset backoff when a fresh token arrives (e.g. after successful refresh)
    reconnectAttempt = 0;
    connectWs(accessToken);
  }, [accessToken, isAuthenticated]);

  return { isConnected, onlineAgents, lastEvent };
}
